import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getSelectedAnswers,
  isVehicleCommercialVehicle,
  paramBuilder
} from "../helpers";
import { getPrePopulatedAnswers } from "../helpers";
import { createMileageSchema } from "./schema";
import {
  getAppraisalData,
  getMileageIsReadyToRender,
  getSourceSystemIsSell,
  getSourceSystemIsBranchAppraisal,
  selectAppraisal,
  getSourceSystemIsCNP,
  getSourceSystemIsCompactView,
  getSourceSystemNameIsSell,
  getQuestionsByCategory
} from "../../store/selectors";
import { useSourceSystem } from "../../hooks/useSourceSystem";
import { appraisalSlice, updateVehicle } from "../../store/slices/Appraisal";
import { useRetryRequest } from "../../hooks/useRetryRequest";
import useReuseAppraisals from "../../hooks/useReuseAppraisals";
import { QuestionData } from "../../data/QuestionData";
import { useLocation, useNavigate } from "react-router-dom";
import { localQuestionsMatchCurrentSetOptions } from "../../helpers/appraisals";
import {
  FactualQuestionsOrder,
  questionRoutingLabels
} from "../../data/QuestionPresentation";
import { clearStorageForNewAppraisal } from "../../sessionAndLocalStorage";
import { sendEvent } from "../../helpers/analytics";

export default function useMileageContainer() {
  const [validationError, setValidationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isMileageValid = false,
    localStorageMileage = "",
    localQuestionsAndAnswers = [],
    isLocalAppraisalToBeUsed = false
  } = useReuseAppraisals();

  const {
    appraisalSessionId,
    questions,
    sourceSystem,
    vehicle,
    userEnteredMileage
  } = useSelector(getAppraisalData);
  const {
    isManual,
    loading: storeLoading,
    isSavedDealReappraisal,
  } = useSelector(selectAppraisal);
  const isSell = useSelector(getSourceSystemIsSell);
  const readyToRender = useSelector(getMileageIsReadyToRender);
  const {
    sourceSystem: { quickValuation }
  } = useSourceSystem();
  const isBranchAppraisal = useSelector(getSourceSystemIsBranchAppraisal);
  const isCnp = useSelector(getSourceSystemIsCNP);
  const isCompactView = useSelector(getSourceSystemIsCompactView);
  const isSourceSystemNameSell = useSelector(getSourceSystemNameIsSell);
  const factualQuestions = useSelector(getQuestionsByCategory("Factual"));

  const formatMileage =
    userEnteredMileage || (isMileageValid ? localStorageMileage : null);

  const [mileage, setMileage] = useState(formatMileage);

  const {
    retryUpdateVehicleRequest,
    questionsRequest,
    appraisalRequest,
    vehicleAndQuestionsRequest
  } = useRetryRequest();

  const firstFactualQuestion = FactualQuestionsOrder[0];

  const { search } = location;
  const urlParams = new URLSearchParams(search);
  const regParam = urlParams.get("registration");
  const idParam = urlParams.get("appraisalSessionId");
  const revisitAppraisals = urlParams.get("revisitAppraisals");

  const handleLanding = () => {
    if (idParam) {
      dispatch(appraisalSlice.actions.setAppraisalSessionId(idParam));
    }
    if (regParam) {
      dispatch(appraisalSlice.actions.setRegistrationInput(regParam));
    }
    if (
      appraisalSessionId &&
      !isBranchAppraisal &&
      regParam &&
      !storeLoading &&
      (!questions || !vehicle)
    ) {
      vehicleAndQuestionsRequest(regParam);
    }
    if (isBranchAppraisal && isSourceSystemNameSell && questions) {
      const { privateHireQuestionId } = QuestionData;
      const selectedPrivateHireAnswer = getSelectedAnswers(
        factualQuestions,
        privateHireQuestionId
      );
      dispatch(
        appraisalSlice.actions.setQuestionsAndAnswers({
          [privateHireQuestionId]: selectedPrivateHireAnswer
        })
      );
    }
  };
  useEffect(handleLanding, [
    dispatch,
    appraisalSessionId,
    location,
    sourceSystem,
    isBranchAppraisal,
    isSourceSystemNameSell,
    questions
  ]);

  const handleFetchAppraisal = () => {
    if (isBranchAppraisal || isManual) {
      appraisalRequest().then(() => {
        questionsRequest();
      });
    }
  };

  useEffect(handleFetchAppraisal, [
    isBranchAppraisal,
    isManual,
    appraisalSessionId
  ]);

  useEffect(() => {
    if (
      quickValuation &&
      !isLocalAppraisalToBeUsed &&
      !isSavedDealReappraisal &&
      readyToRender
    ) {
      const defaultAnswers = getPrePopulatedAnswers(questions);
      dispatch(appraisalSlice.actions.setQuestionsAndAnswers(defaultAnswers));
    }
  }, [dispatch, questions, quickValuation, readyToRender]);

  const handleWrongCar = () => {
    dispatch(appraisalSlice.actions.removeVehicle());
    dispatch(appraisalSlice.actions.removeQuestions());
    clearStorageForNewAppraisal();
    sendEvent("event", "interaction", {
      label: "This isn't my car"
    });
    if (isSell) {
      if (isManual) {
        navigate(`/manual-enquiry?registration=${vehicle.registration}`, {
          replace: true
        });
      } else {
        window.location.assign(process.env.REACT_APP_SELL_HOME);
      }
    } else {
      navigate("/registration");
    }
  };

  const handleSubmit = (mileage, motLastMileage) => {
    dispatch(appraisalSlice.actions.setAnswersChanged(true));
    dispatch(appraisalSlice.actions.setMileage(mileage));
    createMileageSchema(motLastMileage)
      .validate({ mileage })
      .then(() => {
        setLoading(true);

        if (isSell && isVehicleCommercialVehicle(vehicle)) {
          const params = paramBuilder(
            vehicle.registration,
            mileage,
            appraisalSessionId
          );
          window.location.assign(`/value-my-vehicle/sell-my-car/van${params}`);
        }

        const questionSetsMatch = localQuestionsMatchCurrentSetOptions(
          questions,
          localQuestionsAndAnswers
        );

        if (
          (isLocalAppraisalToBeUsed &&
            revisitAppraisals &&
            questionSetsMatch) ||
          isSavedDealReappraisal
        ) {
          dispatch(
            updateVehicle({ mileage: mileage, appraisalSessionId })
          ).then(() => {
            navigate("/estimated-offer");
          });
        } else if (quickValuation) {
          retryUpdateVehicleRequest("/initial-offer", mileage);
        } else if (isCnp || isCompactView) {
          retryUpdateVehicleRequest(`/branch/about`, mileage);
        } else {
          const firstQuestion = questionRoutingLabels[firstFactualQuestion];
          retryUpdateVehicleRequest(`/${firstQuestion}`, mileage);
        }
      })
      .catch(({ message }) => {
        setValidationError(message);
        if (mileage < motLastMileage) {
          sendEvent("event", "app_logic", {
            label: "Mileage must be greater"
          });
        }
      });
  };

  return {
    handleSubmit,
    handleWrongCar,
    readyToRender,
    isSell,
    setMileage,
    validationError,
    isBranchAppraisal,
    loading,
    mileage
  };
}
